import { APP_ENV, BYPASS_AUTH, LOGOUT_URL } from '../constants';
import { OktaAuth, Token } from '@okta/okta-auth-js';
import { clearLocalStorageExceptDMS } from '../util/clearLocalStorage';

enum Environments {
  'us-dit' = 'us-dit',
  'us-stage' = 'us-stage',
  'us-perf' = 'us-perf',
  'us-prod' = 'us-prod',
  'develop' = 'develop',
}

const clientId = {
  'us-dit': '0oa18b1zpbtWJde4q0h8',
  develop: '0oa18b1zpbtWJde4q0h8',
  'us-stage': '0oa1cmt27fan8DvAh0h8',
  'us-perf': '0oa18b1zpbtWJde4q0h8',
  'us-prod': '0oaphqy0vfFEv2fzh1t7',
};

const issuer = {
  'us-dit': 'https://connectcdk-dit.oktapreview.com/oauth2/default',
  develop: 'https://connectcdk-dit.oktapreview.com/oauth2/default',
  'us-perf': 'https://connectcdk-dit.oktapreview.com/oauth2/default',
  'us-stage': 'https://connectcdk-dev.oktapreview.com/oauth2/default',
  'us-prod': 'https://connectcdk.okta.com/oauth2/default',
};

const authorizeUrl = {
  'us-dit': 'https://login-dit.connectcdk.com/login/authorize',
  develop: 'https://login-dit.connectcdk.com/login/authorize',
  'us-perf': 'https://login-dit.connectcdk.com/login/authorize',
  'us-stage': 'https://login-perf.connectcdk.com/login/authorize',
  'us-prod': 'https://login.connectcdk.com/login/authorize',
};

const tokenUrl: Record<Environments, string> = {
  'us-dit': 'https://cml-api-nginx.global-dit.connectcdk.com/token',
  develop: 'https://cml-api-nginx.global-dit.connectcdk.com/token',
  'us-perf': 'https://cml-api-nginx.global-dit.connectcdk.com/token',
  'us-stage': 'https://nginx.nebula-perf.connectcdk.com/api/cs-common-login-api/v1/token',
  'us-prod': 'https://api.connectcdk.com/api/cs-common-login-api/v1/token', // CAR-829 - will be taken care for prod HA IAM change
};

// IDP Proxy URLs (when UNIFY_ENABLE_IDP_PROXY flag is true)
const idpProxyMeUrls: Record<Environments, string> = {
  'us-dit': 'https://idp-proxy.global-dit.connectcdk.com/api/v2/users/me',
  'develop': 'https://idp-proxy.global-dit.connectcdk.com/api/v2/users/me',
  'us-perf': 'https://idp-proxy.global-dit.connectcdk.com/api/v2/users/me',
  'us-stage': 'https://nginx.nebula-perf.connectcdk.com/api/cs-idp-proxy/v1/api/v2/users/me',
  'us-prod': 'https://idp-proxy.global.connectcdk.com/api/v2/users/me',
};

// Direct Okta URLs (when UNIFY_ENABLE_IDP_PROXY flag is false)
const directOktaMeUrls: Record<Environments, string> = {
  'us-dit': 'https://connectcdk-dit.oktapreview.com/api/v1/users/me',
  'develop': 'https://connectcdk-dit.oktapreview.com/api/v1/users/me',
  'us-perf': 'https://connectcdk-dit.oktapreview.com/api/v1/users/me',
  'us-stage': 'https://connectcdk-dev.oktapreview.com/api/v1/users/me',
  'us-prod': 'https://connectcdk.okta.com/api/v1/users/me',
};

const okta = {
  issuer: issuer[APP_ENV as unknown as keyof  typeof Environments],
  clientId: clientId[APP_ENV  as unknown as keyof  typeof Environments],
  redirectUri: `${window.location.origin}/login/callback`,
  authorizeUrl: authorizeUrl[APP_ENV  as unknown as keyof  typeof Environments],
  tokenUrl: tokenUrl[APP_ENV  as unknown as keyof  typeof Environments],
  tokenManager: {
    autoRenew: true,
    syncStorage: true,
    expireEarlySeconds: 60 + Math.floor(Math.random() * 60), // Randomize expireEarlySeconds between 60-120 seconds to prevent token renewal attempts when multiple tabs are open
    storage: localStorage
  },
  scopes: ['openid', 'profile', 'email', 'offline_access']
};

export const OKTA_LOGOUT_URLS: Record<Environments, string> = {
  'us-dit': 'https://login-dit.connectcdk.com/sso/logoff.html?source=unify',
  develop: 'https://login-dit.connectcdk.com/sso/logoff.html?source=unify',
  'us-perf':
    'https://login-dit.connectcdk.com/sso/logoff.html?source=unify',
  'us-stage':
    'https://login-dit.connectcdk.com/sso/logoff.html?source=unify',
  'us-prod': 'https://login.connectcdk.com/sso/logoff.html?source=unify',
};

let hasRunInitCheck = false;

export default function createOktaInstance() {

  if (BYPASS_AUTH) return {};
  
  const oktaAuthInstance = new OktaAuth(okta);
  
  if (!hasRunInitCheck && window.location.pathname !== '/login/callback' && window.location.pathname !== '/logout/callback') {
    hasRunInitCheck = true;
    
    (async () => {
      try {
        const accessToken = await oktaAuthInstance.tokenManager.get('accessToken');
        const idToken = await oktaAuthInstance.tokenManager.get('idToken');

        const hasExpiredToken = !accessToken || !idToken ||
          oktaAuthInstance.tokenManager.hasExpired(accessToken) ||
          oktaAuthInstance.tokenManager.hasExpired(idToken);

        if (hasExpiredToken) {
          console.warn('Stale or missing tokens detected, clearing and redirecting to authorize');
          await oktaAuthInstance.tokenManager.clear();
          clearLocalStorageExceptDMS();
          await oktaAuthInstance.signInWithRedirect({ originalUri: window.location.href });
        }
      } catch (e) {
        console.error('Error checking tokens on init:', e);
        window.location.href = `${LOGOUT_URL}?source=unify`;
      }
    })();
  }
  
  const CROSS_TAB_RENEWAL_LOCK = 'okta-token-renewal-lock';
  const TAB_SYNC_TIMEOUT_MS = 15000; // 15 seconds
  const MAX_REFRESH_RETRIES = 2;                 // 3 total refresh-token attempts
  const REFRESH_RETRY_BACKOFF_MS = [3000, 5000]; // delay before retry 1 and retry 2
  
  // A "definitive" refresh failure means retrying will not help (refresh token unusable
  // or a non-retryable outcome); such failures fall back immediately.
  const isDefinitiveRefreshFailure = (error: unknown): boolean => {
    if ((error as { definitive?: boolean })?.definitive === true) return true; // explicitly marked
    const name = (error as { name?: string })?.name;
    if (name === 'OAuthError') return true; // token endpoint rejected the grant
    const status = (error as { xhr?: { status?: number } })?.xhr?.status;
    if (name === 'AuthApiError' && typeof status === 'number' && status >= 400 && status < 500) {
      // 408 (Request Timeout) and 429 (Too Many Requests) are transient - keep them retryable.
      return status !== 408 && status !== 429;
    }
    return false;
  };

  const originalRenew = oktaAuthInstance.tokenManager.renew.bind(oktaAuthInstance.tokenManager);
  
  oktaAuthInstance.tokenManager.renew = async function(key: string): Promise<Token> {
    let now = Date.now();
    const lock = localStorage.getItem(CROSS_TAB_RENEWAL_LOCK);
    const lockTime = lock ? parseInt(lock, 10) : null;
    
    if (lockTime && !isNaN(lockTime) && (now - lockTime) < TAB_SYNC_TIMEOUT_MS) {
      console.log('Another tab is renewing tokens, waiting for it to finish.');
      
      try {
        const tokens = await this.getTokens();
        const token = tokens[key as keyof typeof tokens];
        if (token && !this.hasExpired(token)) {
          console.log('Token synced from another tab renewal.');
          return token;
        }
      } catch (error) {
        console.error('Error retrieving synced tokens:', error);
      }
      
      // Retry multiple times to allow other tab time to complete
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, 5000)); // Check every 5 seconds
        
        now = Date.now();
        const currentLock = localStorage.getItem(CROSS_TAB_RENEWAL_LOCK);
        const currentLockTime = currentLock ? parseInt(currentLock, 10) : null;
        
        if (!currentLockTime || isNaN(currentLockTime) || (now - currentLockTime) >= TAB_SYNC_TIMEOUT_MS) {
          console.log('Lock released or expired, another tab may have finished or crashed');
          break;
        }
        
        try {
          const tokens = await this.getTokens();
          const token = tokens[key as keyof typeof tokens];
          if (token && !this.hasExpired(token)) {
            console.log('Token synced from another tab renewal.');
            return token;
          }
        } catch (error) {
          console.error('Error retrieving synced tokens:', error);
        }
      }
      
      try {
        const tokens = await this.getTokens();
        const token = tokens[key as keyof typeof tokens];
        if (token && !this.hasExpired(token)) {
          console.log('Token synced from another tab renewal after lock released.');
          return token;
        }
      } catch (error) {
        console.error('Error retrieving synced tokens:', error);
      }
    }
    
    console.log('This tab will renew tokens.');
    localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, now.toString());
    
    try {
      const refreshToken = await this.get('refreshToken');

      if (!refreshToken) {
        console.log('No refresh token available, using original renew method');
        return await originalRenew(key);
      }

      // Attempt renewal with the refresh token, retrying only on transient failures.
      let definitiveFailure = false;
      for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
        try {
          console.log(`Renewing tokens using refresh token (attempt ${attempt + 1})`);
          const freshTokens = await oktaAuthInstance.token.renewTokens();

          // Validate BEFORE persisting: if renewTokens resolved without the requested
          // token, do not write this incomplete set to storage. Retrying will not help
          // (a successful renewal that omits the key won't self-fix), so mark it
          // definitive to fall back immediately.
          const token = freshTokens[key as keyof typeof freshTokens];
          if (!token) {
            throw Object.assign(new Error(`Token ${key} not found after renewal`), { definitive: true });
          }
          await this.setTokens(freshTokens);
          return token;
        } catch (error) {
          definitiveFailure = isDefinitiveRefreshFailure(error);
          const outOfRetries = attempt === MAX_REFRESH_RETRIES;
          if (definitiveFailure || outOfRetries) {
            console.warn('Refresh-token renewal failed; using traditional renewal.', { definitive: definitiveFailure, attempt, error });
            break;
          }
          await new Promise((r) => setTimeout(r, REFRESH_RETRY_BACKOFF_MS[attempt]));
          localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, Date.now().toString()); // keep cross-tab lock fresh
        }
      }

      if (definitiveFailure) {
        console.log('Refresh token is unusable; removing it and renewing via SSO cookie');
        this.remove('refreshToken');
      } else {
        console.log('Falling back to original renew method (refresh token retained)');
      }
      localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, Date.now().toString()); // fallback renewal can be slow; re-stamp lock
      return await originalRenew(key);
    } catch (error) {
      console.error('Token renewal failed:', error);
      throw error;
    } finally {
      localStorage.removeItem(CROSS_TAB_RENEWAL_LOCK);
    }
  };
  
  return oktaAuthInstance;
}

export function getOktaMeUrls(enableIdpProxy: boolean): Record<Environments, string> {
  return enableIdpProxy === false ? directOktaMeUrls : idpProxyMeUrls;
}

// Default export: Direct Okta URLs (as the primary configuration)
export const OKTA_ME_URLS: Record<Environments, string> = directOktaMeUrls;
